// src/multiplayer/services/NetworkManager.ts

import { io, Socket } from 'socket.io-client';
import { ServerResponse, RoomInfo, GameInput } from '../types/multiplayer.types';
import { debugLog, debugWarn } from '../../utils/debug';

export default class NetworkManager {
  private static instance: NetworkManager | null = null;
  private socket: Socket | null = null;
  private roomId: string | null = null;
  private playerName: string = '';
  private isHost: boolean = false;
  private connected: boolean = false;
  private _roomPlayers: Array<{id: string, name: string, isHost: boolean}> = [];
  private rematchRequested: boolean = false;
private opponentRequestedRematch: boolean = false;

  // Event callback storage
  private eventCallbacks: Record<string, Function[]> = {
    'playerJoined': [],
    'playerLeft': [],
    'characterSelected': [],
    'playerReady': [],
    'gameInput': [],
    'gameStart': [],
    'gameEnd': [],
    'connectionChange': [],
    'chatMessage': [],
    'syncGameState': [],
    'rematchRequest': [],
    'cancelRematchRequest': [],
    'bothPlayersReadyForRematch': []
  };
  static isRoomHost: typeof NetworkManager;
  
  /**
   * Singleton pattern
   */
  public static getInstance(): NetworkManager {
    if (!NetworkManager.instance) {
      NetworkManager.instance = new NetworkManager();
    }
    return NetworkManager.instance;
  }
  
  /**
   * Private constructor for singleton pattern
   */
  private constructor() {}

  private teardownSocket(): void {
    if (!this.socket) {
      return;
    }

    this.socket.removeAllListeners();
    this.socket.disconnect();
    this.socket = null;
  }
  
  /**
   * Set player's name
   */
  public setPlayerName(name: string): void {
    this.playerName = name;
  }
  
  /**
   * Get the socket ID of the current player
   */
  public getSocketId(): string {
    return this.socket?.id || '';
  }
  
  /**
   * Get information about all players in the current room
   */
  public getPlayersInRoom(): Array<{id: string, name: string, isHost: boolean}> {
    if (!this._roomPlayers || this._roomPlayers.length === 0) {
      return [];
    }
    return this._roomPlayers;
  }
  
  /**
   * Update players in room (called internally)
   */
  private updatePlayersInRoom(players: Array<{id: string, name: string, isHost: boolean}>) {
    this._roomPlayers = players;
  }
  
  /**
   * Debug helper - log all current event listeners
   */
  public logEventListeners(): void {
    debugLog("Current event listeners:");
    Object.entries(this.eventCallbacks).forEach(([event, callbacks]) => {
      debugLog(`${event}: ${callbacks.length} callbacks`);
    });
  }

  /**
   * Get the current room state (for debugging)
   */
  public getCurrentRoomState(): any {
    return {
      roomId: this.roomId,
      isHost: this.isHost,
      players: this._roomPlayers,
      socketId: this.socket?.id
    };
  }
  
  /**
   * Connect to the multiplayer server
   */
  public connect(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      try {
        this.teardownSocket();

        // For local testing, connect to the local Socket.io server
        const serverUrl = process.env.NODE_ENV === 'production' 
          ? 'wss://your-production-domain.com' // Change this to your production URL
          : 'http://localhost:3001';
        
        // Create socket connection
        this.socket = io(serverUrl, {
          query: {
            playerName: this.playerName
          },
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000
        });
        
        // Set up connection event handlers
        this.socket.on('connect', () => {
          debugLog('Connected to server with ID:', this.socket?.id);
          this.connected = true;
          this.notifyCallbacks('connectionChange', true);
          resolve(true);
        });
        
        this.socket.on('connect_error', (err) => {
          console.error('Connection error:', err);
          this.connected = false;
          this.notifyCallbacks('connectionChange', false);
          reject(err);
        });
        
        // Handle disconnection
        this.socket.on('disconnect', (reason) => {
          debugLog('Disconnected from server:', reason);
          this.connected = false;
          this.notifyCallbacks('connectionChange', false);
          if (reason === 'io server disconnect') {
            // The server has forcefully disconnected the socket
            debugLog('Server forcefully disconnected');
          }
          // If the disconnection is not intentional, try to reconnect
          if (reason === 'transport close' || reason === 'transport error') {
            debugLog('Attempting to reconnect...');
            this.socket?.connect();
          }
        });
        
        // Set up default event listeners
        this.setupEventListeners();
        
      } catch (err) {
        console.error('Failed to connect:', err);
        this.connected = false;
        reject(err);
      }
    });
  }
  
  // First, add rematch functionality to NetworkManager.ts

// Add these properties to the NetworkManager class

// Add these methods to NetworkManager class:

/**
 * Request a rematch after a game is over
 */


public requestRematch(): void {
  if (!this.socket || !this.roomId) {
    console.error('Cannot request rematch: Not connected to a room');
    return;
  }
  
  this.rematchRequested = true;
  debugLog(`Player requesting rematch in room ${this.roomId}`);
  
  this.socket.emit('rematchRequest', {
    roomId: this.roomId,
    timestamp: Date.now()
  }, (response: any) => {
    if (response && !response.success) {
      console.error('Error requesting rematch:', response.error);
      this.rematchRequested = false;
    } else {
      debugLog('Rematch request sent successfully');
    }
  });
}


/**
 * Cancel a rematch request
 */
public cancelRematchRequest(): void {
    if (!this.socket || !this.roomId) {
      console.error('Cannot cancel rematch request: Not connected to a room');
      return;
    }
    
    if (!this.rematchRequested) {
      debugLog('No rematch request to cancel');
      return;
    }
    
    this.rematchRequested = false;
    debugLog(`Player canceling rematch request in room ${this.roomId}`);
    
    this.socket.emit('cancelRematchRequest', {
      roomId: this.roomId,
      timestamp: Date.now()
    });
  }
  
  /**
   * Check if both players have requested a rematch
   */
  public isBothPlayersReadyForRematch(): boolean {
    return this.rematchRequested && this.opponentRequestedRematch;
  }
  
  /**
   * Check if player has requested a rematch
   */
  public hasRequestedRematch(): boolean {
    return this.rematchRequested;
  }
  
  /**
   * Check if opponent has requested a rematch
   */
  public hasOpponentRequestedRematch(): boolean {
    return this.opponentRequestedRematch;
  }
  
  /**
   * Reset rematch state
   */
  public resetRematchState(): void {
    this.rematchRequested = false;
    this.opponentRequestedRematch = false;
  }
/**
 * Cancel a rematch request
  /**
   * Setup default event listeners with improved error handling
   */
  private setupEventListeners(): void {
    if (!this.socket) return;
    this.socket.removeAllListeners();
    
    // Listen for player joined event
    this.socket.on('playerJoined', (data) => {
      debugLog('Player joined event received:', data);
      if (data.players) {
        this.updatePlayersInRoom(data.players);
      }
      this.notifyCallbacks('playerJoined', data);
    });
    
    // Listen for player left event
    this.socket.on('playerLeft', (data) => {
      debugLog('Player left event received:', data);
      if (data.players) {
        this.updatePlayersInRoom(data.players);
      }
      this.notifyCallbacks('playerLeft', data);
    });
    
    // Listen for character selected event with improved logging
    this.socket.on('characterSelected', (data) => {
      debugLog('Character selected event received:', data);
      this.notifyCallbacks('characterSelected', data);
    });
    
    // Listen for player ready event with improved logging
    this.socket.on('playerReady', (data) => {
      debugLog('Player ready state changed event received:', data);
      this.notifyCallbacks('playerReady', data);
    });
    
    // Listen for game input event
    this.socket.on('gameInput', (data) => {
      // Don't log every input to avoid console spam
      this.notifyCallbacks('gameInput', data);
    });
    
    // Listen for game start event with much better logging
// Listen for game start event with enhanced logging
this.socket.on('gameStart', (data) => {
    debugLog('🎮🎮🎮 GAME START EVENT RECEIVED FROM SERVER:', data);
    try {
      this.notifyCallbacks('gameStart', data);
      debugLog('✅ All gameStart callbacks completed successfully');
    } catch (err) {
      console.error('❌ Error in gameStart callbacks:', err);
      // Log the full error details
      if (err instanceof Error) {
        console.error('Error details:', {
          message: err.message,
          stack: err.stack,
          data: data
        });
      } else {
        console.error('Error details:', {
          error: err,
          data: data
        });
      }
    }
  });
    

  // Inside setupEventListeners method, add:
this.socket.on('rematchRequest', (data) => {
    debugLog('Rematch request received from opponent');
    this.opponentRequestedRematch = true;
    this.notifyCallbacks('rematchRequest', data);
    
    // If both players have requested a rematch, notify game
    if (this.rematchRequested && this.opponentRequestedRematch) {
      debugLog('Both players ready for rematch, notifying game');
      this.notifyCallbacks('bothPlayersReadyForRematch', { timestamp: Date.now() });
    }
  });
  
  this.socket.on('cancelRematchRequest', (data) => {
    debugLog('Opponent canceled rematch request');
    this.opponentRequestedRematch = false;
    this.notifyCallbacks('cancelRematchRequest', data);
  });
    // Listen for game end event
    this.socket.on('gameEnd', (data) => {
      debugLog('Game ended event received:', data);
      this.notifyCallbacks('gameEnd', data);
    });
    
    // Listen for chat messages
    this.socket.on('chatMessage', (data) => {
      debugLog('Chat message received:', data);
      this.notifyCallbacks('chatMessage', data);
    });
    
    // Enhanced error handling for socket events
    this.socket.on('error', (error) => {
      console.error('Socket error:', error);
    });
  }
  
  /**
   * Notify all callbacks registered for an event with improved error handling
   */
  private notifyCallbacks(event: string, data: any): void {
    if (!this.eventCallbacks[event]) {
      debugLog(`No callbacks registered for event: ${event}`);
      return;
    }
    
    //debugLog(`Executing ${this.eventCallbacks[event].length} callbacks for event: ${event}`);
    
    this.eventCallbacks[event].forEach((callback, index) => {
      try {
        //debugLog(`Executing callback ${index + 1} for event: ${event}`);
        callback(data);
        //debugLog(`Callback ${index + 1} executed successfully`);
      } catch (err) {
        console.error(`Error in ${event} callback ${index + 1}:`, err);
      }
    });
  }
  
  // Add these methods to your NetworkManager class in NetworkManager.ts
// Place them right before the final closing brace of the class

/**
 * Debug method to execute a test event
 */
public testEventCallback(event: string, testData: any = {}): void {
    //debugLog(`🧪 TESTING EVENT CALLBACK: ${event}`);
    
    if (!this.eventCallbacks[event] || this.eventCallbacks[event].length === 0) {
      debugWarn(`⚠️ No callbacks registered for event: ${event}`);
      return;
    }
    
    debugLog(`Found ${this.eventCallbacks[event].length} callbacks for: ${event}`);
    
    // Create a test data object with relevant info
    const data = {
      ...testData,
      _test: true,
      timestamp: new Date().toISOString(),
      roomId: this.roomId
    };
    
    try {
      this.notifyCallbacks(event, data);
      debugLog(`✅ Test callbacks for ${event} executed successfully`);
    } catch (err) {
      console.error(`❌ Error executing test callbacks for ${event}:`, err);
    }
  }
  
  /**
   * Fix for lost gameStart event - called from the debug panel or global helper
   */
  public forceGameStart(): void {
    if (!this.roomId) {
      console.error('⛔ Cannot force game start: Not in a room');
      return;
    }
    
    debugLog(`🛠️ FORCING GAME START EVENT for room ${this.roomId}`);
    
    // Directly trigger the gameStart event callbacks
    const testData = {
      roomId: this.roomId,
      _forced: true,
      timestamp: new Date().toISOString()
    };
    
    try {
      this.notifyCallbacks('gameStart', testData);
      debugLog('✅ Force game start completed successfully');
    } catch (err) {
      console.error('❌ Error in force game start:', err);
    }
  }
  
  /**
   * Log detailed multiplayer state for debugging
   */
  public logMultiplayerState(): void {
    debugLog('🔍 MULTIPLAYER STATE:');
    debugLog({
      connected: this.connected,
      socketConnected: this.socket?.connected,
      socketId: this.socket?.id,
      roomId: this.roomId,
      isHost: this.isHost,
      playerName: this.playerName,
      players: this._roomPlayers,
      eventCallbacks: Object.fromEntries(
        Object.entries(this.eventCallbacks).map(
          ([event, callbacks]) => [event, callbacks.length]
        )
      )
    });
  }
  /**
   * Disconnect from the multiplayer server
   */
  public disconnect(): void {
    if (this.socket) {
      this.teardownSocket();
      this.roomId = null;
      this.isHost = false;
      this.connected = false;
      this._roomPlayers = [];
      this.notifyCallbacks('connectionChange', false);
    }
  }
  
  /**
   * Check if connected to the server
   */
  public isConnected(): boolean {
    return this.connected && (this.socket?.connected || false);
  }
  
  /**
   * Create a new game room
   */
  public createRoom(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        reject(new Error('Not connected to server'));
        return;
      }
      
      this.socket.emit('createRoom', (response: ServerResponse) => {
        if (response.success) {
          this.roomId = response.roomId;
          this.isHost = true;
          debugLog('Room created:', response.roomId);
          resolve(response.roomId);
        } else {
          console.error('Failed to create room:', response.error);
          reject(new Error(response.error || 'Failed to create room'));
        }
      });
    });
  }
  
  /**
   * Join an existing game room
   */
  public joinRoom(roomId: string): Promise<RoomInfo> {
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        reject(new Error('Not connected to server'));
        return;
      }
      
      this.socket.emit('joinRoom', roomId, (response: ServerResponse) => {
        if (response.success) {
          this.roomId = roomId;
          this.isHost = response.isHost || false;
          debugLog('Joined room:', roomId);
          
          // Update room players if available in response
          if (response.players) {
            this.updatePlayersInRoom(response.players);
          }
          
          resolve(response as unknown as RoomInfo);
        } else {
          console.error('Failed to join room:', response.error);
          reject(new Error(response.error || 'Failed to join room'));
        }
      });
    });
  }
  
  /**
   * Get active rooms from server
   */
  public getActiveRooms(): Promise<RoomInfo[]> {
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        reject(new Error('Not connected to server'));
        return;
      }
      
      this.socket.emit('getRooms', (response: ServerResponse) => {
        if (response.success) {
          resolve(response.rooms || []);
        } else {
          reject(new Error(response.error || 'Failed to get rooms'));
        }
      });
    });
  }
  
  /**
   * Leave the current room
   */
  public leaveRoom(): void {
    if (!this.socket || !this.roomId) return;
    
    this.socket.emit('leaveRoom', { roomId: this.roomId }, (response: ServerResponse) => {
      if (response.success) {
        debugLog('Left room:', this.roomId);
      } else {
        console.error('Error leaving room:', response.error);
      }
      this.roomId = null;
      this.isHost = false;
      this._roomPlayers = [];
    });
  }
  
  /**
   * Get current room ID
   */
  public getCurrentRoomId(): string | null {
    return this.roomId;
  }
  
  /**
   * Check if this client is the host of the room
   */
  public isRoomHost(): boolean {
    return this.isHost;
  }
  
  /**
   * Send game input to other players
   */
  public sendGameInput(input: GameInput): void {
    if (!this.socket || !this.roomId) {
      console.error('Cannot send game input: Not connected to a room');
      return;
    }
    
    this.socket.emit('gameInput', {
      roomId: this.roomId,
      input: {
        ...input,
        timestamp: Date.now() // Add timestamp for ordering
      }
    });
  }
  
  /**
   * Enhanced selectCharacter method with better error handling
   */
  public selectCharacter(characterId: string): void {
    if (!this.socket || !this.roomId) {
      console.error('Cannot select character: Not connected to a room');
      return;
    }
    
    debugLog(`Sending character selection: ${characterId} in room ${this.roomId}`);
    
    this.socket.emit('selectCharacter', {
      roomId: this.roomId,
      characterId
    }, (response: any) => {
      if (response && !response.success) {
        console.error('Error selecting character:', response.error);
      } else {
        debugLog('Character selection confirmed by server');
      }
    });
  }
  
  /**
   * Enhanced setReady method with better error handling
   */
  public setReady(ready: boolean): void {
    if (!this.socket || !this.roomId) {
      console.error('Cannot set ready state: Not connected to a room');
      return;
    }
    
    debugLog(`Sending ready state: ${ready} in room ${this.roomId}`);
    
    this.socket.emit('playerReady', {
      roomId: this.roomId,
      ready
    }, (response: any) => {
      if (response && !response.success) {
        console.error('Error setting ready state:', response.error);
      } else {
        debugLog('Ready state confirmed by server');
      }
    });
  }
  
  /**
   * Enhanced startGame method with better error handling
   */
/**
 * Enhanced startGame method with better error handling and clearer logs
 */
public startGame(): void {
    if (!this.socket || !this.roomId) {
      console.error('⛔ Cannot start game: Not connected to a room');
      return;
    }
    
    if (!this.isHost) {
      console.error('⛔ Cannot start game: Not the host of the room');
      return;
    }
    
    debugLog(`🎮 HOST STARTING GAME in room ${this.roomId}`);
    
    this.socket.emit('startGame', {
      roomId: this.roomId
    }, (response: any) => {
      if (response && !response.success) {
        console.error('❌ Error starting game:', response.error);
      } else {
        debugLog('✅ Game start confirmed by server');
      }
    });
  }
  
  // In NetworkManager.ts, add a debug method
public debugMultiplayerState(): void {
    debugLog("=== MULTIPLAYER DEBUG ===");
    debugLog("Connected:", this.isConnected());
    debugLog("Room ID:", this.roomId);
    debugLog("Is Host:", this.isHost);
    debugLog("Players in room:", this._roomPlayers);
    debugLog("Socket ID:", this.socket?.id);
    debugLog("===========================");
  }
  // Add this method to NetworkManager.ts
// Place it right after the startGame method

/**
 * Enhanced startGame method that includes character data
 * This ensures both players have the same character information
 */
// Add this method to NetworkManager.ts
// Place it right after the startGame method

/**
 * Enhanced startGame method that includes character data for animated models
 * This ensures both players have the same character information
 */
// In NetworkManager.ts, modify the startGameWithCharacters method:
// In NetworkManager.ts, enhance the startGameWithCharacters method
public startGameWithCharacters(player1Character: any, player2Character: any, isMultiplayer: boolean = true): void {
    if (!this.socket || !this.roomId) {
      console.error('⛔ Cannot start game: Not connected to a room');
      return;
    }
    
    if (!this.isHost) {
      console.error('⛔ Cannot start game: Not the host of the room');
      return;
    }
    
    debugLog(`🎮 HOST STARTING GAME in room ${this.roomId} with characters and isMultiplayer=${isMultiplayer}`);
    
    // Log character data being sent
    debugLog("Player 1 character:", player1Character?.id);
    debugLog("Player 2 character:", player2Character?.id);
    
    // Create a safe version of characters with all animation paths preserved
    const safeCharacters = {
      player1: this.sanitizeCharacterWithAnimations(player1Character),
      player2: this.sanitizeCharacterWithAnimations(player2Character)
    };
    
    // Generate a random seed for synchronized randomization
    const seed = Math.floor(Math.random() * 1000000);
    
    // Environment settings - generated by host, used by all
    const environmentSettings = {
      floorIndex: Math.floor(Math.random() * 20), // Random floor index
      backgroundIndex: Math.floor(Math.random() * 10), // Random background index
      skyColor: this.generateRandomGradient(), // Random gradient for sky
      backgroundScale: 15, // Default scale
      backgroundPosition: [0, 2, -2], // Default position
      backgroundRotation: [0, 0, 0], // Default rotation
      showWatermark: true,
      seed: seed // Add seed for any other random decisions
    };
  
    // Store environment settings in window for local reference
    if (typeof window !== 'undefined') {
      window.environmentSync = environmentSettings;
      debugLog("Setting environment sync data:", environmentSettings);
    }
    
    this.socket.emit('startGame', {
      roomId: this.roomId,
      characters: safeCharacters,
      isMultiplayer: isMultiplayer,
      seed: seed,
      environment: environmentSettings,
      timestamp: Date.now() // Add timestamp for ordering
    }, (response: any) => {
      if (response && !response.success) {
        console.error('❌ Error starting game:', response.error);
      } else {
        debugLog('✅ Game start confirmed by server with characters and environment');
      }
    });
  }
  
  // Add this helper method to NetworkManager
  private generateRandomGradient(): string {
    // Create a random hue (0-360)
    const baseHue = Math.floor(Math.random() * 360);
    
    // Create a complementary or analogous hue
    const hueShift = Math.random() > 0.5 
      ? Math.floor(Math.random() * 60) - 30 // Analogous (-30 to +30)
      : 180 + Math.floor(Math.random() * 40) - 20; // Complementary (160-200)
      
    const secondHue = (baseHue + hueShift) % 360;
    
    // Create saturation and lightness values
    const baseSaturation = 30 + Math.floor(Math.random() * 20); // 30-50%
    const baseLightness = 10 + Math.floor(Math.random() * 15); // 10-25%
    
    const topSaturation = baseSaturation - Math.floor(Math.random() * 10); // Slightly less saturated at top
    const topLightness = baseLightness + Math.floor(Math.random() * 10); // Slightly lighter at top
    
    // Create the gradient
    return `linear-gradient(to bottom, 
      hsl(${baseHue}, ${topSaturation}%, ${topLightness}%), 
      hsl(${secondHue}, ${baseSaturation}%, ${baseLightness}%))`;
  }
  
  /**
   * Helper method to sanitize character data for network transmission
   * Preserves all animation paths for animated FBX models
   */
  private sanitizeCharacterWithAnimations(character: any): any {
    if (!character) return null;
    
    // Create a safe copy with all animation paths preserved
    return {
      id: character.id,
      name: character.name,
      imageSrc: character.imageSrc,
      modelPath: character.modelPath,
      restingPath: character.restingPath,
      walkingPath: character.walkingPath,
      attackPath: character.attackPath,
      specialAttackPath: character.specialAttackPath,
      jumpPath: character.jumpPath,
      blockPath: character.blockPath,
      hitPath: character.hitPath,
      headHitPath: character.headHitPath,
      bodyHitPath: character.bodyHitPath,
      victoryPath: character.victoryPath,
      deathPath: character.deathPath,
      punchSound: character.punchSound,
      isFBX: character.isFBX || false,
      color: character.color || ''
    };
  }
  /**
   * End the game (host only) with improved error handling
   */
  public endGame(winner: string): void {
    if (!this.socket || !this.roomId) {
      console.error('Cannot end game: Not connected to a room');
      return;
    }
    
    if (!this.isHost) {
      console.error('Cannot end game: Not the host of the room');
      return;
    }
    
    debugLog(`Host ending game in room ${this.roomId}, winner: ${winner}`);
    
    this.socket.emit('endGame', {
      roomId: this.roomId,
      winner
    }, (response: any) => {
      if (response && !response.success) {
        console.error('Error ending game:', response.error);
      } else {
        debugLog('Game end confirmed by server');
      }
    });
  }
  
  // In NetworkManager.ts, add this method
  public syncGameState(gameState: any): void {
    if (!this.socket || !this.roomId) {
      console.error('Cannot sync game state: Not connected to a room');
      return;
    }
    
    // Add timestamp to avoid processing old updates
    const dataToSend = {
      roomId: this.roomId,
      gameState: {
        ...gameState,
        timestamp: Date.now()
      }
    };
    
    // Send with callback for error handling
    this.socket.emit('syncGameState', dataToSend, (response: any) => {
      if (response && !response.success) {
        console.error('Error syncing game state:', response.error);
      }
    });
  }
  /**
   * Send chat message with improved error handling
   */
  public sendChatMessage(message: string): void {
    if (!this.socket || !this.roomId) {
      console.error('Cannot send message: Not connected to a room');
      return;
    }
    
    this.socket.emit('sendMessage', {
      roomId: this.roomId,
      message
    }, (response: any) => {
      if (response && !response.success) {
        console.error('Error sending message:', response.error);
      }
    });
  }
  
  /**
   * Register event callbacks with improved duplicate checking
   */
// Replace the existing "on" method in NetworkManager.ts (around line 890)
// Replace the on method in NetworkManager.ts
public on(event: string, callback: Function): void {
    if (!this.eventCallbacks[event]) {
      this.eventCallbacks[event] = [];
    }
    
    // Check if this exact function is already registered
    const alreadyRegistered = this.eventCallbacks[event].some(
      existingCallback => existingCallback.toString() === callback.toString()
    );
    
    if (!alreadyRegistered) {
      this.eventCallbacks[event].push(callback);
      debugLog(`Registered callback for event: ${event}, total: ${this.eventCallbacks[event].length}`);
    } else {
      debugLog(`Callback already registered for event: ${event}`);
    }
  }
  
  // Replace the off method in NetworkManager.ts
  public off(event: string, callback: Function): void {
    if (!this.eventCallbacks[event]) {
      debugLog(`No callbacks registered for event: ${event}`);
      return;
    }
    
    const initialLength = this.eventCallbacks[event].length;
    this.eventCallbacks[event] = this.eventCallbacks[event].filter(cb => cb !== callback);
    
    const removed = initialLength - this.eventCallbacks[event].length;
    if (removed > 0) {
      debugLog(`Removed ${removed} callback(s) for event: ${event}`);
    } else {
      debugLog(`No matching callback found for event: ${event}`);
    }
  }
  /**
   * Remove all event callbacks for a specific event
   */
  public offAll(event: string): void {
    if (!this.eventCallbacks[event]) {
      debugWarn(`No callbacks registered for event: ${event}`);
      return;
    }
    
    const removed = this.eventCallbacks[event].length;
    this.eventCallbacks[event] = [];
    
    debugLog(`Removed all ${removed} callback(s) for event: ${event}`);
  }
  
  /**
   * Debug method to check for event listeners
   */
  public hasEventListeners(event: string): boolean {
    return !!(this.eventCallbacks[event] && this.eventCallbacks[event].length > 0);
  }
  
  /**
   * Debug method to get event listener count
   */
  public getEventListenerCount(event: string): number {
    return this.eventCallbacks[event]?.length || 0;
  }
}
